import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useProperty } from '@eti/providers';
import { TripCharacteristics } from '@eti/types';
import { usePrevious } from '@eti/utils';
import { change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import SessionTimeoutDialog from '../../../common/error-modals/SessionTimeoutDialog';
import { PAYMENT } from '../../../constants/formNamesConstants';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../utils/carriers';
import { reduxFields } from '../constants/formFields';
import usePaymentCart from '../hooks/usePaymentCart';
import PaymentCart from '../entry-page/components/PaymentCart';
import { CARD_FORM_SECTION, cardFields } from '../payment-methods/card/constants/formFields';

const {
  INSTALMENT_FEE,
  PRICE_IN_CHARGE_CURRENCY,
  SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT,
  TOTAL_PRICE,
} = reduxFields;

const mapDispatchToProps = (dispatch) => ({
  dispatchPrice(totalPrice) {
    dispatch(change(PAYMENT, TOTAL_PRICE, totalPrice));
  },
  dispatchOtherCurrencyPrice(priceInChargeCurrency) {
    dispatch(change(PAYMENT, PRICE_IN_CHARGE_CURRENCY, priceInChargeCurrency));
  },
});

const mapStateToProps = (state) => ({
  instalmentFee: formValueSelector(PAYMENT)(state, INSTALMENT_FEE),
  installmentOptionId: formValueSelector(PAYMENT)(
    state,
    `${CARD_FORM_SECTION}.${cardFields.CARD_INSTALMENTS}`,
  )?.id,
  shouldAddInstallmentFeeInTotalAmount: formValueSelector(PAYMENT)(
    state,
    SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT,
  ),
});

const PaymentCartContainer = ({
  dispatchPrice,
  bounds,
  dispatchOtherCurrencyPrice,
  instalmentFee,
  ...props
}) => {
  const { p } = useProperty();
  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');
  const { missingCart, totalPrice, priceInChargeCurrency, ...cartData } = usePaymentCart(props);
  const prevTotalPrice = usePrevious(totalPrice);
  const prevOtherCurrencyPrice = usePrevious(priceInChargeCurrency);
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  useEffect(() => {
    if (dispatchPrice && totalPrice !== prevTotalPrice) {
      dispatchPrice(totalPrice);
    }
  }, [totalPrice, prevTotalPrice, dispatchPrice]);

  useEffect(() => {
    if (dispatchOtherCurrencyPrice && priceInChargeCurrency !== prevOtherCurrencyPrice) {
      dispatchOtherCurrencyPrice(priceInChargeCurrency);
    }
  }, [priceInChargeCurrency, prevOtherCurrencyPrice, dispatchOtherCurrencyPrice]);

  if (missingCart) {
    return <SessionTimeoutDialog />;
  }

  return (
    <PaymentCart
      {...props}
      {...cartData}
      bounds={bounds}
      instalmentFee={instalmentFee}
      marketingCarrierNames={marketingCarrierNames}
      priceInChargeCurrency={priceInChargeCurrency}
      totalPrice={totalPrice}
    />
  );
};

PaymentCartContainer.propTypes = {
  bounds: PropTypes.arrayOf(
    PropTypes.shape({
      boundId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      includedCheckedBaggage: PropTypes.object,
      segments: PropTypes.array,
    }),
  ),
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.object),
      title: PropTypes.string,
    }),
  ),
  dispatchOtherCurrencyPrice: PropTypes.func,
  dispatchPrice: PropTypes.func,
  installmentOptionId: PropTypes.number,
  instalmentFee: PropTypes.number,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  serviceFee: PropTypes.shape({
    currency: PropTypes.shape({
      code: PropTypes.string,
      exponent: PropTypes.number,
      name: PropTypes.string,
    }),
    value: PropTypes.number,
  }),
  setRefetch: PropTypes.func,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentCartContainer);
